import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Breadcrumbs,
  Link,
  Stack,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EmailIcon from '@mui/icons-material/Email';
import { membershipApplicationAPI, membershipTierAPI, memberProfileAPI, userAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { MembershipApplication, ApplicationStatus, UserRole } from '../../types';
import { useAuth } from '../../context/AuthContext';

const MembershipApplicationReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState<MembershipApplication | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string>('');

  // States for rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');

  // States for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState<string>('');

  // States for resend email
  const [resendingEmail, setResendingEmail] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check if user is admin or superadmin
  const isAdminOrSuperAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  // Fetch application details
  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const response = await membershipApplicationAPI.getApplicationById(id);
        setApplication(response.data);

        // If application is approved and has an approver, fetch approver's name
        if (response.data.status === ApplicationStatus.APPROVED && response.data.approvedByUserId) {
          try {
            const userResponse = await userAPI.getUserById(response.data.approvedByUserId);
            setApproverName(`${userResponse.data.firstName} ${userResponse.data.lastName}`);
          } catch (userErr) {
            console.error('Error fetching approver details:', userErr);
            setApproverName('Unable to load approver name');
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching application details:', err);
        setError('Failed to load application details');
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id]);

  // Navigate to create member account page
  const handleCreateAccount = () => {
    if (!id) return;
    navigate(`/create-member-account/${id}`);
  };

  // Handle application rejection dialog open
  const handleRejectDialogOpen = () => {
    setRejectDialogOpen(true);
  };

  // Handle application rejection
  const handleReject = async () => {
    if (!id || !rejectionReason.trim()) return;

    try {
      await membershipApplicationAPI.rejectApplication(id, rejectionReason);
      // Redirect back to applications list after rejection
      navigate('/membership-applications');
    } catch (err) {
      console.error('Error rejecting application:', err);
      setError('Failed to reject application');
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
  };

  // Handle delete dialog open
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmationText('');
  };

  // Handle application deletion
  const handleDelete = async () => {
    if (!id || deleteConfirmationText.toUpperCase() !== 'APPROVE') return;

    try {
      await membershipApplicationAPI.deleteApplication(id);
      // Redirect back to applications list after deletion
      navigate('/membership-applications');
    } catch (err) {
      console.error('Error deleting application:', err);
      setError('Failed to delete application');
    }
  };

  // Handle resend email
  const handleResendEmail = async () => {
    if (!id) return;

    setResendingEmail(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await membershipApplicationAPI.resendApplicationEmail(id);
      setSuccessMessage(
        application?.status === ApplicationStatus.APPROVED
          ? 'Approval email resent successfully'
          : 'Rejection email resent successfully'
      );
    } catch (err) {
      console.error('Error resending email:', err);
      setError('Failed to resend email');
    } finally {
      setResendingEmail(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: string | number | undefined) => {
    if (amount === undefined) return '$0.00';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
  };

  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <RouterLink
            to={"/dashboard"}
            className="user-link"
            style={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.primary',
              textDecoration: 'none'
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </RouterLink>

          <RouterLink
            color="inherit"
            to="/membership-applications"
            className="user-link"
            style={{
              color: 'text.primary',
              textDecoration: 'none',
            }}
          >
            Applications
          </RouterLink>
          <Typography color="text.primary">Application Review</Typography>
        </Breadcrumbs>

        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
          Membership Application Review
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : application ? (
        <>
          {application.status === ApplicationStatus.APPROVED && (
            <Paper sx={{
              p: 3,
              mb: 3,
              border: '2px solid',
              borderColor: 'success.main',
              backgroundColor: 'background.paper'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'success.main' }}>
                  Application Approved
                </Typography>
                {isAdminOrSuperAdmin && (
                  <Tooltip title="Resend approval email">
                    <IconButton
                      color="primary"
                      onClick={handleResendEmail}
                      disabled={resendingEmail}
                      size="small"
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'success.main' }}>
                      Approved At
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {application.approvalDate ?
                        new Date(application.approvalDate).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Not available'
                      }
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'success.main' }}>
                      Approved By
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {approverName || 'Loading...'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          )}

          {application.status === ApplicationStatus.REJECTED && (
            <Paper sx={{
              p: 3,
              mb: 3,
              border: '2px solid',
              borderColor: 'error.main',
              backgroundColor: 'background.paper'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'error.main' }}>
                  Application Rejected
                </Typography>
                {isAdminOrSuperAdmin && (
                  <Tooltip title="Resend rejection email">
                    <IconButton
                      color="primary"
                      onClick={handleResendEmail}
                      disabled={resendingEmail}
                      size="small"
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'error.main' }}>
                    Rejection Reason
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {application.rejectionReason || 'No reason provided'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Applicant Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Submitted On</Typography>
                  <Typography variant="body1">
                    {new Date(application.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at {new Date(application.createdAt).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                  <Typography variant="body1">{`${application.firstName} ${application.lastName}`}</Typography>
                </Box>

                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                  <Typography variant="body1">
                    <Link href={`mailto:${application.email}`} color="primary">
                      {application.email}
                    </Link>
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Phone</Typography>
                  <Typography variant="body1">{application.phone || 'Not provided'}</Typography>
                </Box>

                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Mobile</Typography>
                  <Typography variant="body1">{application.mobile || 'Not provided'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">Address</Typography>
                  <Typography variant="body1">
                    {application.addressLine1 || 'Not provided'}
                    {application.addressLine2 && `, ${application.addressLine2}`}
                    {' • '}
                    {[
                      application.city,
                      application.province,
                      application.postalCode
                    ].filter(Boolean).join(', ')}
                    {application.country && `, ${application.country}`}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            mb: 3
          }}>
            <Paper sx={{ p: 3, flex: 1, width: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Membership Requested
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Membership Tier</Typography>
                    <Typography variant="body1">{application.membershipTierName}</Typography>
                  </Box>

                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Monthly Fee</Typography>
                    <Typography variant="body1">{formatCurrency(application.monthlyPrice)}</Typography>
                  </Box>
                </Box>

                {application.additionalFees && application.additionalFees.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Additional Fees</Typography>
                    {application.additionalFees.map((fee, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          {fee.name}: {formatCurrency(fee.amount)} {fee.required ? '(Required)' : '(Optional)'}
                        </Typography>
                        {fee.description && <Typography variant="caption">{fee.description}</Typography>}
                      </Box>
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, flex: 1, width: '100%' }}>
              <Typography variant="h5" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Former Member</Typography>
                    <Typography variant="body1">{application.isFormerMember ? 'Yes' : 'No'}</Typography>
                  </Box>

                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Student</Typography>
                    <Typography variant="body1">{application.isStudent ? 'Yes' : 'No'}</Typography>
                  </Box>

                  <Box sx={{ minWidth: '200px' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Referred By</Typography>
                    <Typography variant="body1">
                      {application.isReferred ? application.referrerName || 'Yes (Name not provided)' : 'No'}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">Corporate Group Size</Typography>
                  <Typography variant="body1">{(application.corporateGroupSize == null) ? 'Not provided' : application.corporateGroupSize}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">Reason for Joining</Typography>
                  <Typography variant="body1">
                    {application.reasonForJoining || 'Not provided'}
                  </Typography>
                </Box>

              </Stack>
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/membership-applications')}
            >
              Back to Applications
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {application.status === ApplicationStatus.PENDING && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateAccount}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRejectDialogOpen}
                  >
                    Reject Application
                  </Button>
                </>
              )}
              {isAdminOrSuperAdmin && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteDialogOpen}
                >
                  Delete Application
                </Button>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography>Application not found</Typography>
        </Paper>
      )}

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejection. This will be sent to the applicant.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="rejectionReason"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim()}
          >
            Reject Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Warning:</strong> This is an irreversible operation that will cause data loss. 
            The application will be permanently deleted and cannot be recovered.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            To confirm deletion, please type <strong>APPROVE</strong> in the text box below:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="deleteConfirmation"
            label="Type APPROVE to confirm"
            type="text"
            fullWidth
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            placeholder="APPROVE"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteConfirmationText.toUpperCase() !== 'APPROVE'}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  );
};

export default MembershipApplicationReviewPage;
